import React, {
  FC,
  useMemo,
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import CircularProgress from "@pixel/core/CircularProgress";
import ContentModal from "@pixel/core/ContentModal";
import LayoutRootSection from "@pixel/internal/LayoutRootSection";
import Typography from "@mui/material/Typography";
import TableChartIcon from "@pixel/assets/icons/TableChart";
import ListBase, { ListSearchChangeProps } from "@pixel/core/ListBase";
import DataTable from "@pixel/core/DataTable";
import Box from "@pixel/core/Box";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
  IReqsResultData,
  IPayloadParams,
  IReqsData,
  IActionModalProps,
} from "../../types/candidates";
import CandidateListContext from "../../contexts/candidate/candidateListContext";
import {
  transformReqsFilterData,
  reqsListStatusFilter,
  reqsListCategoryFilter,
  transformReqsListData,
} from "../../utils/candidateResultTransform";
import axios from "axios";
import { useSnackbar } from "@pixel/core/SnackbarService";
import FloatingCircularProgress from "./FloatingCircularProgress";

const AddReqsActionModal: FC<IActionModalProps> = ({ open, onClose }) => {
  const snack = useSnackbar();
  const [perPageCount, setPerPageCount] = useState(10);
  const [openModal, setOpenModal] = React.useState(false);
  const [mainModalLoading, setMainModalLoading] = useState(false);
  const [addedCandidates, setAddedCandidates] = React.useState({});
  const handleClose = (event:any, reason:any) => {
    if (reason && reason == "backdropClick") 
      return;
    setOpenModal(false);
    onClose();
  }
  const initialReqsPayload: IPayloadParams = {
    page: 0,
    q: null,
    maxResult: perPageCount,
    jobStateValue: 1,
    mine: null,
    categoryIds: null,
  };

  const [results, setCandidateResult] = useState<IReqsResultData>({
    nodes: [],
    totalCount: 0,
    resultsCount: 0,
    showCompany: null,
  });
  const [reqsPayload, setReqsPayload] = useState<IPayloadParams>(
    initialReqsPayload
  );
  const [totalCandidates, setTotalCandidates] = useState(0);
  const candidateContext = useContext(CandidateListContext);
  const baseReqsURL = window.jvUrls.jhire + "/action/requisitions";

  const handleResolve = () => {
    setMainModalLoading(true);
    let addCandPayload = {
      jobIds: selected,
      appIds: candidateContext.selectedCandidate,
    };
    try {
      candidateContext.props.candidateService.addCandidate(
        addCandPayload,
        (response: any) => {
          setMainModalLoading(false);
          try {
            if (response.data["Server_Error:"]) {
              snack.showError(candidateContext.props.translations.SERVER_ERROR);
            } else {
              if (Object.keys(response.data.duplicateCandidates).length > 0) {
                setOpenModal(true);
                setAddedCandidates(response.data);
              } else {
                let successText =
                  candidateContext.selectedCandidate.length > 1
                    ? candidateContext.props.translations
                        .CANDIDATES_ADDED_MULTIPLE
                    : candidateContext.props.translations.CANDIDATES_ADDED_SINGLE;
                snack.showSuccess(successText);
                onClose();
              }
            }
          } catch(error) {
            snack.showError(candidateContext.props.translations.SERVER_ERROR);
          }
        });
    } catch (error) {
      console.log(error);
      snack.showError(candidateContext.props.translations.SERVER_ERROR);
      setMainModalLoading(false);
    }

    return true;
  };

  useEffect(() => {
    async function fetchRequisitions() {
      try {
        axios
          .get(baseReqsURL, {
            params: reqsPayload,
          })
          .then((response) => {
            setCandidateResult(transformReqsListData(response.data));
            setTotalCandidates(response.data.jobCount);
            setPagingContext({
              hideFirstLastActions: true,
              perPage: perPageCount,
              page: reqsPayload.page + 1,
              first:
                response.data.jobCount > 0
                  ? reqsPayload.page * perPageCount + 1
                  : 0,
              last: perPageCount * reqsPayload.page + response.data.jobs.length,
            });
          });
      } catch (error) {
        console.log(error);
      }
    }
    fetchRequisitions();
  }, [reqsPayload]);

  useEffect(() => {
    async function populateReqsModal() {
      try {
        await candidateContext.props.candidateService.getRequistionFilters(
          null,
          (response: any) => {
            if (response.status === 200 && response.data) {
              transformReqsFilterData(response.data);
            }
          }
        );
      } catch (error) {
        console.log(error);
      }
    }
    populateReqsModal();
  }, []);

  const handleSearch = (props: ListSearchChangeProps) => {
    let updatePayload: IPayloadParams = {
      page: 0,
      q: props.q ? props.q : null,
      maxResult: 10,
      jobStateValue: null,
      mine: null,
      categoryIds: null,
    };
    props.appliedSearchFilters.forEach((property: any) => {
      if (property && property.searchFilterId === "category") {
        updatePayload.categoryIds = property.filterValues[0].value;
      } else {
        updatePayload.mine = property.filterValues[0].mine;
        updatePayload.jobStateValue = property.filterValues[0].value;
      }
    });
    setReqsPayload(updatePayload);
  };

  const searchFitlerStatus = useMemo(
    () => ({
      id: "status",
      label: candidateContext.props.translations.STATUS,
      type: "single_select",
      options: reqsListStatusFilter,
    }),
    []
  );

  const searchFitlerCategory = useMemo(
    () => ({
      id: "category",
      label: candidateContext.props.translations.CATEGORY,
      type: "single_select",
      options: reqsListCategoryFilter,
    }),
    []
  );

  const appliedSearchFilterStatus = useMemo(
    () => ({
      searchFilterId: "status",
      displayLabel: candidateContext.props.translations.STATUS + ": Open",
      filterValues: [{ value: 1, label: "Open" }],
    }),
    []
  );

  const [appliedSearchFilters, setAppliedSearchFilters] = useState([
    appliedSearchFilterStatus,
  ]);
  const searchFilters = [searchFitlerStatus, searchFitlerCategory];
  const [selected, setSelected] = useState<Array<IReqsData["EId"]>>([]);

  const handleSelection = useCallback(
    (node: IReqsData, nextSelectedState: boolean) => {
      setSelected([node.EId]);
    },
    [selected, setSelected]
  );

  const [pagingContext, setPagingContext] = useState({
    hideFirstLastActions: true,
    perPage: perPageCount,
    page: 0,
    first: 0,
    last: totalCandidates <= perPageCount ? totalCandidates : perPageCount,
  });

  const onPaginationChange = (event: any, currPage: number) => {
    setReqsPayload({
      ...reqsPayload,
      page: currPage - 1,
    });
  };

  return (
    <div>
      <ContentModal
        id="add-candidate"
        open={open}
        onClose={onClose}
        showClose={true}
        sizeVariant="md"
        title={candidateContext.props.translations.ADD_TO_REQUISITION}
        labelCloseAria={candidateContext.props.translations.CLOSE}
        rejectActions={[
          {
            reason: "cancel",
            label: candidateContext.props.translations.HIRE_CANCEL,
          },
        ]}
        resolveActions={[
          {
            reason: "ok",
            label: candidateContext.props.translations.ADD_TO_REQUISITION,
          },
        ]}
        onResolve={handleResolve}
      >
        <FloatingCircularProgress open={mainModalLoading} />
        <LayoutRootSection top transparent>
        <Typography variant="body2" sx={{ pb: 2 }}>
            {candidateContext.selectedCandidate.length +
              " " +
              candidateContext.props.translations.I18N_CANDIDATES_COMMON +
              " " +
              candidateContext.props.translations
                .I18N_CANDIDATES_ADD_TO_REQ_MSG}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, pb: 2 }}>
            {
              candidateContext.props.translations
                .APPLICANT_JOBLIST_SELECT_A_REQUISITION
            }
          </Typography>
          <ListBase
            results={results}
            showSearch={true}
            labelSearch={candidateContext.props.translations.SEARCH}
            labelSearchAria={candidateContext.props.translations.SEARCH}
            // @ts-ignore
            nodeIdKey="EId"
            selectedIds={selected}
            onSearch={handleSearch}
            selectable
            onSelection={handleSelection}
            selectionType="single"
            searchFilters={searchFilters}
            appliedSearchFilters={appliedSearchFilters}
            pagingContext={pagingContext}
            onPageChanged={onPaginationChange}
            viewContexts={[
              {
                ariaLabel:
                  candidateContext.props.translations.ADD_TO_REQUISITION,
                icon: <TableChartIcon />,
                renderResults: (viewContextProps) => (
                  <DataTable
                    {...viewContextProps}
                    getRowID={(node) => node.EId}
                    columns={[
                      {
                        id: "requisitions",
                        columnName:
                          candidateContext.props.translations
                            .COMMON_REQUISITIONS,
                        minWidth: 80,
                        renderCell: (node) => (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {node.title}
                          </Typography>
                        ),
                      },
                      {
                        id: "id",
                        columnName:
                          candidateContext.props.translations.ADDTOREQ_ID,
                        renderCell: (node) => (
                          <Typography variant="body2">
                            {node.requisitionId}
                          </Typography>
                        ),
                        minWidth: 80,
                      },
                      {
                        id: "status",
                        columnName: candidateContext.props.translations.STATUS,
                        renderCell: (node) => (
                          <Typography variant="body2">
                            {node.jobState}
                          </Typography>
                        ),
                        minWidth: 150,
                      },
                      {
                        id: "category",
                        columnName:
                          candidateContext.props.translations.CATEGORY,
                        renderCell: (node) => (
                          <Typography variant="body2">
                            {node.category && node.category.name}
                          </Typography>
                        ),
                        minWidth: 150,
                      },
                      {
                        id: "locations",
                        columnName:
                          candidateContext.props.translations.COMMON_LOCATION,
                        renderCell: (node) => (
                          <Typography variant="body2">
                            {node.location && node.location.name}
                          </Typography>
                        ),
                        minWidth: 80,
                      },
                    ]}
                    labelCheckboxAriaLabel="Select candidate with name {label}"
                    labelCheckboxKey="name"
                  />
                ),
              },
            ]}
          />
        </LayoutRootSection>
      </ContentModal>
      {openModal && (
        <ContentModal
        id="add-candidate-confirmation"
        open={openModal}
        onClose={handleClose}
        showClose={true}
        sizeVariant="xs"
        title={candidateContext.props.translations.ADD_TO_REQUISITION}
        labelCloseAria={candidateContext.props.translations.CLOSE}
        rejectActions={[
          {
            reason: "cancel",
            label: candidateContext.props.translations.HIRE_CANCEL,
          },
        ]}
        resolveActions={[
          {
            reason: "ok",
            label: candidateContext.props.translations.COMMON_OK,
          },
        ]}
        onResolve={handleClose}
      >
        <Box >
          <Typography id="keep-mounted-modal-description">
            {addedCandidates.count == 0
              ? candidateContext.props.translations
                  .CANDIDATES_NOT_ADDED_TO_REQ +
                " " +
                candidateContext.props.translations
                  .CANDIDATES_ALREADY_ASSOCIATED_TO_REQ
              : addedCandidates.count +
                " " +
                candidateContext.props.translations.CANDIDATES_ADDED_MULTIPLE +
                " " +
                candidateContext.props.translations
                  .CANDIDATES_ALREADY_ASSOCIATED_TO_REQ}
          </Typography>
          <List sx={candidateContext.modalStyle.listStyle}>
            {Object.keys(addedCandidates.duplicateCandidates || {}).map(
              (key, index) => (
                <ListItem
                  key={key}
                  disableGutters
                  sx={candidateContext.modalStyle.itemStyle}
                >
                  <ListItemText
                    primary={`${addedCandidates.duplicateCandidates[key]}`}
                  />
                </ListItem>
              )
            )}
          </List>
        </Box>
      </ContentModal>
      )}
    </div>
  );
};

export default AddReqsActionModal;
